/**
 * F-025 — Audio system (replaces F-016 inline audio).
 *
 * One AudioContext, master gain controlled by Settings.volume + muted.
 * Three SFX categories (launch · merge · gameover) all generated
 * procedurally so we ship zero assets. BGM placeholder (silent for now,
 * future: an OscillatorBank-driven ambient drone) — facade is in place
 * so post-MVP can swap implementations.
 */

import {
  setMuted as setInlineMuted,
  isMuted as isInlineMuted,
} from "./inline.ts";

export type SfxName = "launch" | "merge" | "gameover";

export interface AudioSystem {
  setVolume(value: number): void;
  setMuted(value: boolean): void;
  play(name: SfxName, opts?: { intensity?: number }): boolean;
  startBgm(): void;
  stopBgm(): void;
  dispose(): void;
  /**
   * F-091 진단/직접 unlock — UI 가 'tap to enable sound' 버튼에서 호출.
   * iOS 가 인정하는 click 이벤트 stack frame 에서 바로 ctx 생성 + resume +
   * 짧은 audible test tone 재생. 자동 listener 가 어떤 이유로든 실패해도
   * 이 명시 호출이 보장된 unlock path. 성공시 true.
   */
  unlockNow(): boolean;
  /** F-091 진단 — 현재 audio 시스템 상태. 디버그 UI 가 표시. */
  diagnostics(): AudioDiagnostics;
}

export interface AudioDiagnostics {
  ctxState: "uninitialized" | AudioContextState;
  sampleRate: number | null;
  mediaSessionPromoted: boolean;
  audioSessionType: string | null;
}

interface InternalState {
  ctx: AudioContext | null;
  master: GainNode | null;
  volume: number;
  muted: boolean;
  bgmPlaying: boolean;
}

const DEFAULT_VOLUME = 0.7;

/**
 * iOS Safari 는 `new AudioContext()` 를 항상 `suspended` 로 시작하고, 사용자
 * 제스처에서 `ctx.resume()` 가 명시 호출돼야만 소리를 낸다. 데스크톱 Chrome 은
 * 자동 unlock 하지만 iOS 는 그렇지 않음.
 *
 * 핵심 — 사용자가 처음 화면을 탭/스와이프하는 그 콜스택 안에서 resume()
 * 호출이 일어나야 iOS 가 "fresh gesture" 로 인정한다. listener 가 ctx 생성
 * 후에 깔리면 첫 제스처를 놓치고 두 번째 이후에야 unlock 되는 버그가 됨
 * (사용자 보고: "처음엔 안 들리다가 게임 도중 어느 순간부터 들림"). 그래서
 * audio system construction 시점에 listener 를 미리 깔고, 첫 제스처 callback
 * 안에서 ctx 를 (필요시) 생성 + resume + silent buffer warm-up 모두 동기로
 * 수행한다.
 */
function attemptResume(ctx: AudioContext): void {
  if (typeof ctx.resume === "function" && ctx.state === "suspended") {
    void ctx.resume().catch(() => {
      // ignored — iOS may reject if not in a gesture, retried next call
    });
  }
}

function playSilentBuffer(ctx: AudioContext): void {
  try {
    const buf = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(ctx.destination);
    src.start(0);
  } catch {
    // ignored — silent buffer is belt-and-braces, real SFX still works
  }
}

/**
 * iOS WebKit (Safari · iOS Chrome — same engine) 가 Web Audio output 을
 * **ringer 채널**로 라우팅하는 기본 동작이 있어, 무음 스위치 / Control
 * Center mute / 낮은 ringer 볼륨에 묶인다. Web Audio 가 미디어 채널로
 * 라우팅되려면 페이지가 한 번이라도 HTMLAudioElement 의 audio data 를
 * 재생해 "media playback session" 으로 승격돼야 함. 사용자 첫 제스처
 * 안에서 짧은 silent WAV blob 을 <audio>.play() — Promise 가 실패해도
 * 무관 (다음 호출에 재시도). Android Chrome 은 이 라우팅 분리가 없어서
 * iOS 만 영향 받음.
 */
function buildSilentWavBlobUrl(durationMs = 100): string | null {
  if (typeof Blob === "undefined" || typeof URL === "undefined") return null;
  const sampleRate = 22050;
  const numSamples = Math.max(1, Math.floor((sampleRate * durationMs) / 1000));
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);
  view.setUint32(0, 0x52494646, false); // "RIFF"
  view.setUint32(4, 36 + numSamples * 2, true);
  view.setUint32(8, 0x57415645, false); // "WAVE"
  view.setUint32(12, 0x666d7420, false); // "fmt "
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  view.setUint32(36, 0x64617461, false); // "data"
  view.setUint32(40, numSamples * 2, true);
  // samples remain zero-filled (silent)
  return URL.createObjectURL(new Blob([buffer], { type: "audio/wav" }));
}

let mediaSessionAudio: HTMLAudioElement | null = null;
let mediaSessionPromoted = false;

function promoteToMediaSession(): void {
  if (mediaSessionPromoted) return;
  if (typeof document === "undefined" || typeof Audio === "undefined") return;
  if (!mediaSessionAudio) {
    const url = buildSilentWavBlobUrl();
    if (!url) return;
    const el = new Audio();
    // playsInline + muted=false + low volume — iOS only promotes when
    // it would be audible (muted=true 는 promotion 자격 박탈 보고).
    el.src = url;
    (el as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
    el.setAttribute("playsinline", "");
    el.setAttribute("webkit-playsinline", "");
    el.preload = "auto";
    el.muted = false;
    el.volume = 0.001;
    mediaSessionAudio = el;
  }
  // Returns a Promise — must be awaited or caught. We fire-and-forget
  // since the promise rejection means iOS will retry on next gesture.
  void mediaSessionAudio.play().then(
    () => {
      mediaSessionPromoted = true;
    },
    () => {
      // iOS rejects when not in a gesture — retried on next pointerdown.
    },
  );
}

interface GestureUnlockHooks {
  /** Called inside the first user gesture; must return ctx synchronously. */
  ensureCtx(): AudioContext | null;
}

function installBootGestureUnlock(hooks: GestureUnlockHooks): () => void {
  if (typeof window === "undefined") return () => undefined;
  // iOS Safari/Chrome (둘 다 WebKit) 는 click/touchend 를 "fresh user
  // activation" 으로 가장 잘 인정. pointerdown/touchstart 도 받지만
  // 일부 iOS 버전에서 거부 보고. 모두 등록해서 첫 발화에 unlock.
  const events = [
    "pointerdown",
    "touchstart",
    "touchend",
    "mousedown",
    "click",
    "keydown",
  ] as const;
  let removed = false;
  const remove = (): void => {
    if (removed) return;
    removed = true;
    for (const ev of events) window.removeEventListener(ev, handler, true);
  };
  const handler = (): void => {
    const ctx = hooks.ensureCtx();
    if (ctx) {
      attemptResume(ctx);
      playSilentBuffer(ctx);
    }
    // iOS — Web Audio 는 ringer 채널로 라우팅돼서 silent 스위치 / 낮은
    // ringer 볼륨에 묶임. <audio> 요소를 한 번이라도 play() 해야 페이지
    // 가 media playback session 으로 승격되어 미디어 채널로 라우팅됨.
    promoteToMediaSession();
    if (mediaSessionPromoted) {
      // 한 번만 성공해도 충분 — listener 는 떼도 됨.
      remove();
    } else {
      // promoteToMediaSession() 의 Promise 가 비동기 — 첫 제스처 직후
      // 잠깐 listener 유지해 다음 제스처에서 재시도 가능하게 함. 단,
      // ctx 자체는 이미 unlock 됐으므로 재시도는 ringer→media 전환만.
    }
  };
  for (const ev of events) {
    // capture: true 로 캔버스의 stopPropagation/preventDefault 영향 회피.
    window.addEventListener(ev, handler, { passive: true, capture: true });
  }
  return remove;
}

/**
 * iOS 16.4+ AudioSession API — Web Audio 를 명시적으로 'playback' 세션 타입
 * 으로 분류해 ringer 채널 라우팅을 회피. 미지원 브라우저는 무시.
 */
function configureAudioSession(ctx: AudioContext): void {
  const ext = ctx as AudioContext & {
    audioSession?: { type?: string };
  };
  if (ext.audioSession && typeof ext.audioSession === "object") {
    try {
      ext.audioSession.type = "playback";
    } catch {
      // 미지원 / 읽기전용 — 무시
    }
  }
}

function ensureContext(state: InternalState): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (state.ctx) return state.ctx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  state.ctx = new Ctor();
  configureAudioSession(state.ctx);
  state.master = state.ctx.createGain();
  state.master.gain.setValueAtTime(state.muted ? 0 : state.volume, 0);
  state.master.connect(state.ctx.destination);
  return state.ctx;
}

function applyMasterGain(state: InternalState): void {
  if (!state.master || !state.ctx) return;
  const target = state.muted ? 0 : state.volume;
  state.master.gain.setTargetAtTime(target, state.ctx.currentTime, 0.01);
}

function playLaunchSfx(ctx: AudioContext, master: GainNode, intensity: number): void {
  const now = ctx.currentTime;
  const duration = 0.18 + intensity * 0.18;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(220 + intensity * 160, now);
  osc.frequency.exponentialRampToValueAtTime(80, now + duration);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.4, now + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  osc.connect(gain).connect(master);
  osc.start(now);
  osc.stop(now + duration + 0.05);
}

function playMergeSfx(ctx: AudioContext, master: GainNode, intensity: number): void {
  const now = ctx.currentTime;
  // Bright pluck — sine + slight noise
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(440 + intensity * 300, now);
  osc.frequency.exponentialRampToValueAtTime(280, now + 0.15);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.5, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
  osc.connect(gain).connect(master);
  osc.start(now);
  osc.stop(now + 0.25);
}

function playGameoverSfx(ctx: AudioContext, master: GainNode, _intensity: number): void {
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(220, now);
  osc.frequency.exponentialRampToValueAtTime(40, now + 0.9);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.45, now + 0.05);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.95);
  osc.connect(gain).connect(master);
  osc.start(now);
  osc.stop(now + 1.0);
}

export interface CreateAudioSystemOptions {
  /** Test seam — supply a context (helpful when window.AudioContext is absent). */
  contextFactory?: () => AudioContext | null;
  initialVolume?: number;
  initialMuted?: boolean;
}

export function createAudioSystem(
  options: CreateAudioSystemOptions = {},
): AudioSystem {
  const state: InternalState = {
    ctx: null,
    master: null,
    volume: options.initialVolume ?? DEFAULT_VOLUME,
    muted: options.initialMuted ?? false,
    bgmPlaying: false,
  };

  const tryContext = (): AudioContext | null => {
    if (options.contextFactory) {
      if (state.ctx) return state.ctx;
      const ctx = options.contextFactory();
      if (!ctx) return null;
      state.ctx = ctx;
      state.master = ctx.createGain();
      state.master.gain.setValueAtTime(state.muted ? 0 : state.volume, 0);
      state.master.connect(ctx.destination);
      return ctx;
    }
    return ensureContext(state);
  };

  // Pre-install the gesture unlock listener at construction time — BEFORE
  // any user input. The handler runs synchronously inside the first
  // pointerdown/touchend/click and triggers ctx creation + resume + silent
  // buffer warm-up. iOS Safari only counts the unlock if everything happens
  // within the gesture stack frame; lazy ctx creation in `play()` misses
  // the first gesture and audio stays silent until later interactions.
  const removeUnlock = installBootGestureUnlock({ ensureCtx: tryContext });

  // Sync legacy inline mute flag so any leftover F-016 calls behave.
  setInlineMuted(state.muted);

  const setVolume = (value: number): void => {
    state.volume = Math.max(0, Math.min(1, value));
    applyMasterGain(state);
  };

  const setMuted = (value: boolean): void => {
    state.muted = value;
    setInlineMuted(value || isInlineMuted());
    applyMasterGain(state);
  };

  const play = (name: SfxName, opts?: { intensity?: number }): boolean => {
    if (state.muted) return false;
    const ctx = tryContext();
    if (!ctx || !state.master) return false;
    // iOS — if the gesture-unlock listener fired but resume was rejected
    // (rare race), retry on every play. Cheap noop when already running.
    attemptResume(ctx);
    const intensity = Math.max(0, Math.min(1, opts?.intensity ?? 0.5));
    switch (name) {
      case "launch":
        playLaunchSfx(ctx, state.master, intensity);
        return true;
      case "merge":
        playMergeSfx(ctx, state.master, intensity);
        return true;
      case "gameover":
        playGameoverSfx(ctx, state.master, intensity);
        return true;
    }
  };

  const startBgm = (): void => {
    state.bgmPlaying = true;
  };

  const stopBgm = (): void => {
    state.bgmPlaying = false;
  };

  const dispose = (): void => {
    removeUnlock();
    state.master?.disconnect();
    if (state.ctx?.state !== "closed") {
      void state.ctx?.close?.();
    }
    state.ctx = null;
    state.master = null;
  };

  /**
   * F-091 진단/직접 unlock — UI 의 explicit "tap to enable sound" 버튼이
   * 호출. iOS 가 절대 인정하는 click handler 안에서 ctx 생성 + resume +
   * 짧지만 audible 한 test tone 으로 unlock 을 강제. 자동 unlock 이 어떤
   * 이유로든 실패해도 이 path 가 보장.
   */
  const unlockNow = (): boolean => {
    const ctx = tryContext();
    if (!ctx || !state.master) return false;
    attemptResume(ctx);
    promoteToMediaSession();
    // 짧지만 들리는 test tone — iOS 가 audible playback 으로 인정해야 unlock
    // confirmation 이 떨어지는 케이스 대비. 200Hz · 80ms · volume 0.15.
    const now = ctx.currentTime;
    const lookahead = 0.02; // iOS 가 가끔 currentTime=0 직후 스케줄 무시
    const start = now + lookahead;
    const dur = 0.08;
    try {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(440, start);
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.18, start + 0.005);
      g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      osc.connect(g).connect(state.master);
      osc.start(start);
      osc.stop(start + dur + 0.02);
    } catch {
      // 일부 환경에서 createOscillator 가 throw — 무시
    }
    return ctx.state === "running";
  };

  const diagnostics = (): AudioDiagnostics => {
    const ext = state.ctx as
      | (AudioContext & { audioSession?: { type?: string } })
      | null;
    return {
      ctxState: state.ctx ? state.ctx.state : "uninitialized",
      sampleRate: state.ctx?.sampleRate ?? null,
      mediaSessionPromoted,
      audioSessionType: ext?.audioSession?.type ?? null,
    };
  };

  return {
    setVolume,
    setMuted,
    play,
    startBgm,
    stopBgm,
    dispose,
    unlockNow,
    diagnostics,
  };
}

export const __testing = { DEFAULT_VOLUME };
